// Simplified, parametric HVAC load / CFM / equipment-sizing engine — the
// "Design" layer on top of Room Takeoff. Approximates (does not replace) the
// engineering team's full ASHRAE psychrometric AHU workbook: every formula
// below is a deliberate simplification of the equivalent calc in that
// workbook, noted inline, so it stays traceable and can be tightened later.
// Pure functions only, no DB access — same convention as
// electricalCircuitEngine.js and calcRoom() (takeoffMath.js): everything is
// derived live from stored inputs, never snapshotted.
//
// Lighting heat gain is deliberately NOT read from a mechanical-only input —
// it reads the Electrical discipline's own lighting_fixture_count criterion
// and EstimateCostSettings.VaPerLightingFixture, so a lighting change on the
// Electrical side is reflected here automatically instead of needing a
// second, divergent mechanical entry.

import { calcRoom } from "./takeoffMath";
import { effectiveCriterion, criterionKey } from "../config/takeoffCriteria";
import { calcLightingSystem } from "./roomSystemsEngine";

// ---- constants --------------------------------------------------------------

// BTUH/person, sensible only — ASHRAE typical for light/seated activity.
export const BTUH_PER_PERSON = 245;

// W -> BTUH
export const W_TO_BTUH = 3.413;

// Approximate U-factor (BTUH/hr/sf/°F) per architectural wall_system option —
// a coarse stand-in for a true assembly U-value; good enough for budgetary
// envelope load, not a substitute for a wall-section calc.
export const WALL_U_FACTOR = {
  'uPVC 3" honeycomb': 0.09,
  'Aluminum honeycomb 2"': 0.12,
  '1/2" liner panel (one side)': 0.25,
  "MgO panel": 0.15,
  FRP: 0.20,
  "Stick-built / drywall": 0.08,
  "By others": 0.15,
};
const DEFAULT_WALL_U_FACTOR = 0.15;

// Flat multiplier folded onto the envelope conduction load to stand in for
// solar gain through glazing/roof, since this engine doesn't model glass
// area/SHGF/CLF the way the workbook's RM0x tabs do.
export const SOLAR_ALLOWANCE_MULTIPLIER = 1.15;

// Standard motor sizes (HP), ascending — replaces the workbook's full
// motor-efficiency table lookup with "round up to the next standard size."
export const STANDARD_MOTOR_SIZES_HP = [1, 1.5, 2, 3, 5, 7.5, 10, 15, 20, 25, 30, 40, 50, 60, 75, 100];

// ---- small helpers ------------------------------------------------------------

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function roomCriterion(criteria, disc, fieldKey, room, fallback = 0) {
  const { value } = effectiveCriterion(criteria, disc, fieldKey, room);
  return value === "" || value === undefined ? fallback : num(value, fallback);
}

// Project-wide design assumptions (ΔT, safety factor, design temps, ...) are
// stored with RoomID = null — no room-type resolution chain applies to them.
function projectCriterion(criteria, fieldKey, fallback) {
  const raw = criteria[criterionKey("mech", fieldKey, null)];
  return raw === undefined || raw === "" ? fallback : num(raw, fallback);
}

// ---- room heat gain -----------------------------------------------------------

// Cross-discipline read: lighting load comes from Electrical's own lighting
// design, not a separate mechanical field. Since 2026-09-11 that design is
// roomSystemsEngine.js's EFFECTIVE fixture count (override ?? lumen-method
// calculation ?? legacy default) × the selected fixture's real wattage from
// Basis of Design; VaPerLightingFixture is only the fallback when the fixture
// option carries no watts/VA — so a lighting change on the Electrical side
// (fixture type, light level, override) flows straight into the heat gain.
// systemsCtx = { bodAttrs, tunables } (settings is taken from elecSettings).
export function computeLightingBtuh(room, elecCriteria, elecSettings, systemsCtx = {}) {
  const lighting = calcLightingSystem(room, elecCriteria || {}, { ...systemsCtx, settings: systemsCtx.settings ?? elecSettings });
  return lighting.totalWatts * W_TO_BTUH;
}

// Approximates workbook rows Y23/Y33/Y42 (solar + wall/roof + misc
// conduction) as one flat exterior-wall-SF x U-factor x ΔT term, with a
// conservative multiplier standing in for solar gain.
export function computeEnvelopeBtuh(room, criteria) {
  const exteriorWallFt = roomCriterion(criteria, "mech", "exterior_wall_ft", room, 0);
  if (exteriorWallFt <= 0) return 0;
  const wallSystem = effectiveCriterion(criteria, "arch", "wall_system", room).value;
  const uFactor = WALL_U_FACTOR[wallSystem] ?? DEFAULT_WALL_U_FACTOR;
  const outdoorSummerDb = projectCriterion(criteria, "outdoor_summer_db", 95);
  const indoorDesignTemp = projectCriterion(criteria, "indoor_design_temp", 68);
  const deltaT = Math.max(0, outdoorSummerDb - indoorDesignTemp);
  const wallSf = exteriorWallFt * num(room.HeightFt, 10);
  return wallSf * uFactor * deltaT * SOLAR_ALLOWANCE_MULTIPLIER;
}

// Approximates workbook cells Y54 (load subtotal) / Y56 (+ safety factor).
// equipmentHeatGainBtuh is the sum of that room's ProcessEquipment
// connections' HeatGainBTUH (an aggregation the caller does, since it
// requires joining across equipment/connections — not this module's concern).
export function computeRoomHeatGainBtuh(room, criteria, elecCriteria, elecSettings, equipmentHeatGainBtuh = 0, systemsCtx = {}) {
  const occupancy = roomCriterion(criteria, "mech", "occupancy_count", room, 0);
  const peopleBtuh = occupancy * BTUH_PER_PERSON;
  const lightingBtuh = computeLightingBtuh(room, elecCriteria, elecSettings, systemsCtx);
  const envelopeBtuh = computeEnvelopeBtuh(room, criteria);
  const equipmentBtuh = num(equipmentHeatGainBtuh, 0);
  const subtotalBtuh = peopleBtuh + lightingBtuh + envelopeBtuh + equipmentBtuh;
  const safetyFactorPct = projectCriterion(criteria, "safety_factor_pct", 10) / 100;
  return {
    peopleBtuh, lightingBtuh, envelopeBtuh, equipmentBtuh, subtotalBtuh,
    totalBtuh: subtotalBtuh * (1 + safetyFactorPct),
  };
}

// ---- three-method room CFM ------------------------------------------------------

// Approximates workbook cell Z35: MAX(ACH-based, heat-gain-based, exhaust
// make-up), rounded to the nearest 5 CFM.
export function computeRoomCfm(room, criteria, heatGainBtuh) {
  const ach = num(room.ACH);
  const achCfm = ach > 0
    ? calcRoom({
        length: num(room.LengthFt), width: num(room.WidthFt), height: num(room.HeightFt),
        ach, areaOverrideFt2: room.areaOverrideFt2,
      }).cfm
    : 0;

  const supplyAirDeltaT = projectCriterion(criteria, "supply_air_delta_t", 15);
  const heatGainCfm = supplyAirDeltaT > 0 ? num(heatGainBtuh) / (1.08 * supplyAirDeltaT) : 0;

  const exhaustCfm = roomCriterion(criteria, "mech", "exhaust_cfm", room, 0);

  const candidates = [
    { method: "ach", cfm: achCfm },
    { method: "heatGain", cfm: heatGainCfm },
    { method: "exhaust", cfm: exhaustCfm },
  ];
  const governing = candidates.reduce((a, b) => (b.cfm > a.cfm ? b : a));
  const finalCfm = Math.round(governing.cfm / 5) * 5;
  return { achCfm, heatGainCfm, exhaustCfm, finalCfm, governingMethod: governing.cfm > 0 ? governing.method : "ach" };
}

// ---- AHU-zone rollup (Room -> AHU) --------------------------------------------

// Groups rooms by TakeoffAHUZones.AHUZoneID (the existing zone == the AHU
// record) and sums their live CFM/heat-gain, then divides by the zone's own
// active units (Qty - StandbyUnits) for a per-unit design size — "1 AHU
// serving 5 rooms" is the Qty=1 case; "2 AHUs, 1 standby" divides the same
// total load across 1 active unit.
export function computeAhuZoneRollup(rooms, criteria, elecCriteria, elecSettings, zonesById = {}, equipmentHeatGainByRoomId = {}, systemsCtx = {}) {
  const byZone = new Map();
  for (const room of rooms) {
    if (!room.AHUZoneID) continue;
    const equipBtuh = equipmentHeatGainByRoomId[room.RoomID] || 0;
    const heatGain = computeRoomHeatGainBtuh(room, criteria, elecCriteria, elecSettings, equipBtuh, systemsCtx);
    const cfm = computeRoomCfm(room, criteria, heatGain.totalBtuh);
    if (!byZone.has(room.AHUZoneID)) {
      byZone.set(room.AHUZoneID, { AHUZoneID: room.AHUZoneID, zone: zonesById[room.AHUZoneID], rooms: [], totalCfm: 0, totalBtuh: 0 });
    }
    const entry = byZone.get(room.AHUZoneID);
    entry.rooms.push({ room, heatGain, cfm });
    entry.totalCfm += cfm.finalCfm;
    entry.totalBtuh += heatGain.totalBtuh;
  }
  return [...byZone.values()].map(entry => {
    const qty = Math.max(1, num(entry.zone?.Qty, 1));
    const standby = Math.max(0, num(entry.zone?.StandbyUnits, 0));
    const activeUnits = Math.max(1, qty - standby);
    return {
      ...entry,
      tonnage: entry.totalBtuh / 12000,
      activeUnits,
      perUnitCfm: entry.totalCfm / activeUnits,
      perUnitTonnage: (entry.totalBtuh / 12000) / activeUnits,
    };
  });
}

// ---- plant-equipment rollup (AHU -> central plant) -----------------------------

// Rolls AHU-zone totals up to whatever plant equipment serves them
// (TakeoffAHUZones.ServedByEquipmentID -> ProcessEquipment.EquipmentID),
// applying the plant equipment's own active/standby split — e.g. one
// chiller-and-pump package (Qty=2, StandbyUnits=1: "N+1") serving 3 AHUs.
export function computePlantEquipmentRollup(ahuZoneRollups, equipmentById = {}) {
  const byPlant = new Map();
  for (const zoneRollup of ahuZoneRollups) {
    const plantId = zoneRollup.zone?.ServedByEquipmentID;
    if (!plantId) continue;
    if (!byPlant.has(plantId)) {
      byPlant.set(plantId, { EquipmentID: plantId, equipment: equipmentById[plantId], zones: [], totalCfm: 0, totalBtuh: 0 });
    }
    const entry = byPlant.get(plantId);
    entry.zones.push(zoneRollup);
    entry.totalCfm += zoneRollup.totalCfm;
    entry.totalBtuh += zoneRollup.totalBtuh;
  }
  return [...byPlant.values()].map(entry => {
    const qty = Math.max(1, num(entry.equipment?.Qty, 1));
    const standby = Math.max(0, num(entry.equipment?.StandbyUnits, 0));
    const activeUnits = Math.max(1, qty - standby);
    const tonnage = entry.totalBtuh / 12000;
    return { ...entry, tonnage, activeUnits, perUnitCfm: entry.totalCfm / activeUnits, perUnitTonnage: tonnage / activeUnits };
  });
}

// ---- coil / humidifier / fan sizing (per AHU) ---------------------------------

// Approximates the PREHEAT COIL / REHEAT COIL tabs' capacity math — a BTUH
// number for budgetary equipment sizing, not a full coil selection
// (rows/fins/GPM).
export function computeCoilLoads(ahuTotalCfm, outsideAirPct, criteria) {
  const oaPct = num(outsideAirPct, 0.3);
  const oaCfm = num(ahuTotalCfm) * oaPct;
  const indoorDesignTemp = projectCriterion(criteria, "indoor_design_temp", 68);
  const outdoorWinterDb = projectCriterion(criteria, "outdoor_winter_db", 10);
  const reheatRise = projectCriterion(criteria, "reheat_rise_default", 10);
  const preheatBtuh = Math.max(0, oaCfm * 1.08 * (indoorDesignTemp - outdoorWinterDb));
  const reheatBtuh = num(ahuTotalCfm) * 1.08 * reheatRise;
  return { oaCfm, preheatBtuh, reheatBtuh };
}

// Magnus-formula approximation of saturation vapor pressure -> humidity ratio
// in grains/lb dry air. A simplified stand-in for a full psychrometric chart
// lookup — accurate to within a few percent near typical HVAC design
// conditions, which is all a budgetary humidifier capacity needs.
export function approxSaturationGrains(tempF, rhPct) {
  const tempC = ((tempF - 32) * 5) / 9;
  const satVaporPressureHpa = 6.1094 * Math.exp((17.625 * tempC) / (tempC + 243.04));
  const vaporPressureHpa = (rhPct / 100) * satVaporPressureHpa;
  const atmPressureHpa = 1013.25;
  const humidityRatio = (0.622 * vaporPressureHpa) / (atmPressureHpa - vaporPressureHpa);
  return humidityRatio * 7000; // grains/lb dry air
}

// Approximates the HUMIDIFIER / HMD LOAD01 tabs — capacity in lb/hr steam
// needed to bring outside air from winter design conditions up to the
// indoor humidity target. Outdoor winter air is assumed near-saturated
// (typical cold-dry design condition) since no outdoor design RH is tracked.
export function computeHumidifierCapacityLbHr(ahuOutsideAirCfm, criteria) {
  const indoorTemp = projectCriterion(criteria, "indoor_design_temp", 68);
  const indoorRh = projectCriterion(criteria, "indoor_design_rh", 40);
  const outdoorWinterDb = projectCriterion(criteria, "outdoor_winter_db", 10);
  const indoorGrains = approxSaturationGrains(indoorTemp, indoorRh);
  const outdoorGrains = approxSaturationGrains(outdoorWinterDb, 80);
  const grainsDiff = Math.max(0, indoorGrains - outdoorGrains);
  const airDensityLbPerFt3 = 0.075;
  return num(ahuOutsideAirCfm) * 60 * airDensityLbPerFt3 * (grainsDiff / 7000);
}

// Approximates EST BHP / EST MHP — fan brake horsepower from CFM x TSP at an
// assumed fan efficiency, rounded up to the next standard motor size instead
// of the workbook's full motor-efficiency table lookup.
export function computeFanMotorSizing(ahuTotalCfm, criteria) {
  const tsp = projectCriterion(criteria, "ahu_default_tsp", 4);
  const fanEfficiencyPct = projectCriterion(criteria, "fan_efficiency_pct", 65) / 100;
  const bhp = fanEfficiencyPct > 0 ? (num(ahuTotalCfm) * tsp) / (6356 * fanEfficiencyPct) : 0;
  const motorHp = STANDARD_MOTOR_SIZES_HP.find(hp => hp >= bhp) ?? STANDARD_MOTOR_SIZES_HP[STANDARD_MOTOR_SIZES_HP.length - 1];
  return { bhp, motorHp };
}

// ---- AHU cooling-coil load (scenario plant demand basis) ---------------------------
// System Architecture & Scenarios Phase C (product decision 2026-09-13): the
// plant demand basis is the GREATER of (a) the served rooms' sensible tons —
// today's zone `tonnage` — and (b) the AHU cooling-coil load, which adds the
// outside-air sensible load, the outside-air latent load and fan heat.
// Budgetary: 1.08 × CFM × ΔT sensible, 0.68 × CFM × Δgrains latent (Magnus
// grains helper), fan heat = BHP × 2545 BTUH/hp (draw-through, all to the
// coil). Outside-air latent needs the outdoor summer RH criterion; without it
// the latent term is UNRESOLVED (null, warning) — never a hidden default. An
// absent outdoor summer DB is assumed 95 °F and flagged.
export const BTUH_PER_HP = 2545;
export function computeAhuCoolingCoilLoad(zoneRollup, criteria) {
  const zone = zoneRollup?.zone || {};
  const totalCfm = num(zoneRollup?.totalCfm, 0), roomSensibleBtuh = num(zoneRollup?.totalBtuh, 0);
  const oaPct = zone.OutsideAirPct != null ? num(zone.OutsideAirPct, 0.3) : (zone.AHUType === "oa100" ? 1 : 0.3);
  const oaCfm = totalCfm * oaPct;
  const warnings = [];
  const hasOutdoorDb = projectCriterion(criteria, "outdoor_summer_db", null) !== null;
  const outdoorDb = projectCriterion(criteria, "outdoor_summer_db", 95);
  if (!hasOutdoorDb) warnings.push({ code: "outdoor_summer_db_assumed", message: "Outdoor summer design DB not set — 95 °F assumed for the outside-air load." });
  const indoorDb = projectCriterion(criteria, "indoor_design_temp", 68);
  const indoorRh = projectCriterion(criteria, "indoor_design_rh", 50);
  const outdoorRh = projectCriterion(criteria, "outdoor_summer_rh", null);
  const oaSensibleBtuh = 1.08 * oaCfm * Math.max(0, outdoorDb - indoorDb);
  let oaLatentBtuh = null;
  if (outdoorRh !== null) oaLatentBtuh = 0.68 * oaCfm * Math.max(0, approxSaturationGrains(outdoorDb, outdoorRh) - approxSaturationGrains(indoorDb, indoorRh));
  else if (oaCfm > 0) warnings.push({ code: "oa_latent_unresolved", message: "Outdoor summer RH not set — the outside-air latent load is not included in the coil load (add the criterion to resolve it)." });
  const fan = computeFanMotorSizing(totalCfm, criteria);
  const fanHeatBtuh = fan.bhp * BTUH_PER_HP;
  const coilBtuh = roomSensibleBtuh + oaSensibleBtuh + (oaLatentBtuh ?? 0) + fanHeatBtuh;
  const roomSensibleTons = roomSensibleBtuh / 12000, coilTons = coilBtuh / 12000;
  const governing = coilTons >= roomSensibleTons ? "coil" : "room";
  return {
    totalCfm, oaPct, oaCfm, roomSensibleBtuh, roomSensibleTons, oaSensibleBtuh, oaLatentBtuh, fanHeatBtuh, fanBhp: fan.bhp,
    coilBtuh, coilTons, governing, governingTons: Math.max(roomSensibleTons, coilTons), governingBtuh: Math.max(roomSensibleBtuh, coilBtuh), warnings,
  };
}
